import React, { FC, useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';
import { Column, FormInput, Row } from 'Shared/components';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import { FormValuesType } from 'QuoteApp/services/endpoints/GetDetails';
import { useEdit, useFieldData } from 'QuoteApp/utils/contexts';
import { getFieldErrors } from 'QuoteApp/utils';
import { VehicleFormFieldKeys } from '../options';
import { VehicleBusinessLabel } from './VehicleBusinessLabel';

type BusinessUseFormProps = {
  config: GetConfigResponseType['vehicles'];
  vehicleId: string;
};

export const BusinessUseForm: FC<BusinessUseFormProps> = ({
  config,
  vehicleId,
}) => {
  const {
    setFieldValue,
    handleChange,
    handleBlur,
    touched,
    errors,
    values,
    initialValues,
  } = useFormikContext<FormValuesType>();

  const { editingId } = useEdit();
  const { alwaysShowErrors, serverErrors, editableFields } = useFieldData();
  const readOnly = editingId !== vehicleId;

  const getFullFieldName = useCallback(
    (fieldName: VehicleFormFieldKeys) => `vehicles.${vehicleId}.${fieldName}`,
    [vehicleId]
  );

  const getInputProps = useCallback(
    (fieldName: VehicleFormFieldKeys, required?: boolean) => {
      const isRequired =
        required !== undefined
          ? required
          : fieldName === 'businessUse'
            ? (config.businessUse?.required ?? true)
            : true;
      const fullFieldName = getFullFieldName(fieldName);
      return {
        name: fullFieldName,
        onChange: handleChange,
        onBlur: handleBlur,
        value: values.vehicles[vehicleId][fieldName],
        disabled: readOnly || !getIn(editableFields, fullFieldName),
        errorMessage: getFieldErrors<FormValuesType>(
          fullFieldName,
          isRequired,
          alwaysShowErrors,
          errors,
          serverErrors,
          values,
          touched,
          initialValues
        ),
      };
    },
    [
      editableFields,
      initialValues,
      serverErrors,
      alwaysShowErrors,
      readOnly,
      errors,
      getFullFieldName,
      handleBlur,
      handleChange,
      touched,
      values,
      vehicleId,
      config.businessUse?.required,
    ]
  );

  const isBusinessUseTypeRequired =
    values.vehicles?.[vehicleId]?.businessUse === 'true';

  return (
    <Row>
      <Column variant="third">
        <VehicleBusinessLabel label="Is vehicle used for any business purposes?" />
        <FormInput
          {...getInputProps('businessUse')}
          variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
          data-testid="business-use"
          label=""
          onChange={(value: string) =>
            setFieldValue(getFullFieldName('businessUse'), value)
          }
          options={{ true: 'Yes', false: 'No' }}
        />
      </Column>
      {isBusinessUseTypeRequired && (
        <Column variant="third">
          <VehicleBusinessLabel label="Business use type" />
          <FormInput
            name={`vehicles.${vehicleId}.businessUseType`}
            onBlur={handleBlur}
            value={values.vehicles?.[vehicleId]?.businessUseType}
            errorMessage={getFieldErrors<FormValuesType>(
              `vehicles.${vehicleId}.businessUseType`,
              isBusinessUseTypeRequired,
              alwaysShowErrors,
              errors,
              serverErrors,
              values,
              touched,
              initialValues
            )}
            label=""
            variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
            disabled={
              editingId !== vehicleId ||
              !getIn(editableFields, `vehicles.${vehicleId}.businessUseType`)
            }
            data-testid="vehicle-business-use-type"
            onChange={(value: string) => {
              setFieldValue(`vehicles.${vehicleId}.businessUseType`, value);
            }}
            options={config.businessUseType || {}}
          />
        </Column>
      )}
    </Row>
  );
};
